import { useRef, useState } from "react"
import TableHead from "@common/table/head/tableHead"
import TableHeadToolbar from "@common/table/toolbar/tableTopToolbar"
import { useTableInstance } from "@common/table/hooks/useTable"
import { useTableOptions } from "@common/table/hooks/useTableOptions"
import TableBottomToolbar from "@common/table/toolbar/tableBottomToolbar"
import { RTable, ReactTableProps } from "@common/table/types"
import Skeleton from "@common/skeletons/skeleton"
import { TableBody } from "@common/table/body/tableBody"
import TableFooter from "@common/table/footer/tableFooter"
import { isEmpty, isNullOrEmpty } from "@common/lib/util"
import { useVirtualizer } from "@tanstack/react-virtual"
const ReactTable = ({
  commonActions,
  multiSelectActions,
  onMultiSelectActionClick,
  onRowClick,
  onSearch,
  footerLink,
  showFooterLink,
  footerLinkText,
  renderEmptyState,
  wrapRowInLink,
  getLinkHref,
  ...options
}: ReactTableProps) => {
  const tableOptions = useTableOptions(options)
  const table = useTableInstance(tableOptions) as RTable
  const searchText = table.refs.searchInputRef.current
  const isDataEmpty = isEmpty(options.data) && !searchText
  const [expandedRowId, setExpandedRowId] = useState<string>() // use to keep expand state for row details
  /* 
  const columnSizeVars = useMemo(() => {
    const headers = getFlatHeaders()
    const colSizes: { [key: string]: number } = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]
      const colSize = header.getSize()
      colSizes[`--header-${parseCSSVarId(header.id)}-size`] = colSize
      colSizes[`--col-${parseCSSVarId(header.column.id)}-size`] = colSize
    }
    return colSizes
  }, [columns, columnSizing, columnSizingInfo, columnVisibility])
 */
  const parentRef = useRef(null)

  const rowVirtualizer = options.enableRowVirtualization
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useVirtualizer({
        count: table.getRowModel().rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 40,
      })
    : undefined

  const virtualRows = rowVirtualizer ? rowVirtualizer.getVirtualItems() : undefined
  const totalHeight = rowVirtualizer ? rowVirtualizer.getTotalSize() : undefined

  return (
    <div
      className="border  rounded-md flex flex-col h-full "
      ref={table.refs.tableContainerRef}
    >
      {!isDataEmpty && (
        <TableHeadToolbar
          {...{
            table,
            commonActions,
            onSearch,
            multiSelectActions,
            onMultiSelectActionClick,
          }}
        />
      )}
      <div className="w-full h-full flex flex-col overflow-auto" ref={parentRef}>
        <>
          {/* Render empty state if data is an empty array */}
          {isDataEmpty && renderEmptyState?.()}
          {/* Show skeleton if data is null or undefined */}
          {!options.data && <Skeleton name="TableSkeleton" />}
          {/* If search query is not null or data is not empty or null then render the table  */}
          {(!isNullOrEmpty(options.data) || (searchText && options.data)) && (
            <table
              className={table.options.themes.tableClasses}
              ref={table.refs.tableRef}
            >
              <TableHead table={table} />
              <TableBody
                {...{
                  table,
                  onRowClick,
                  expandedRowId,
                  setExpandedRowId,
                  wrapRowInLink,
                  getLinkHref,
                  virtualRows,
                  totalHeight,
                  rowVirtualizer,
                }}
              />
              <TableFooter
                {...{ table, footerLink, showFooterLink, footerLinkText }}
              />
            </table>
          )}
        </>
      </div>
      {!isDataEmpty && <TableBottomToolbar table={table} />}
    </div>
  )
}

export default ReactTable
